import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import adminApi from "../../api/adminApi";

const FIELD_SX = {
  "& .MuiInputBase-root": {
    height: 40,
  },
};

const DAYS = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

const formatDay = (day) =>
  day ? day.charAt(0) + day.slice(1).toLowerCase() : "";

// Rent rate card: one rate per (year, campus, residentType).
const RENT_RESIDENT_TYPES = ["student", "adult", "staff"];

export default function CampusEditor({
  campuses,
  loading,
  onRefresh,
  setBanner,
  selectedYear,
}) {
  // Rent rates for the selected year, keyed by campus name -> { student, adult, staff }.
  const [rentRates, setRentRates] = useState({});
  const [rentLoading, setRentLoading] = useState(false);
  const [rentSaving, setRentSaving] = useState(false);

  // The campus list holds every campus across all years (one record per year),
  // so for the rent grid scope to campuses operating in the selected year and
  // dedupe by name — otherwise the same campus shows twice and prior-year ones
  // (e.g. a campus not running this year) appear.
  const rentCampuses = useMemo(() => {
    const yearStr = String(selectedYear || "");
    const inYear = (campuses || []).filter((c) => {
      if (c.active === false) return false;
      return (
        String(c.arrivalDate || "").startsWith(yearStr) ||
        String(c.departureDate || "").startsWith(yearStr)
      );
    });
    const byName = new Map();
    inYear.forEach((c) => {
      if (!byName.has(c.name)) byName.set(c.name, c);
    });
    return [...byName.values()].sort((a, b) =>
      String(a.name).localeCompare(String(b.name))
    );
  }, [campuses, selectedYear]);

  const loadRentRates = useCallback(async () => {
    if (!selectedYear) return;
    setRentLoading(true);
    try {
      const data = await adminApi.getRentCosts(selectedYear);
      const next = {};
      rentCampuses.forEach((c) => {
        next[c.name] = { student: "", adult: "", staff: "" };
      });
      (Array.isArray(data) ? data : []).forEach(({ campus, residentType, rate }) => {
        const t = String(residentType || "").toLowerCase();
        if (!next[campus]) next[campus] = { student: "", adult: "", staff: "" };
        if (RENT_RESIDENT_TYPES.includes(t)) next[campus][t] = rate ?? "";
      });
      setRentRates(next);
    } catch (err) {
      console.error(err);
      setBanner({ type: "error", text: "Failed to load rent rates." });
    } finally {
      setRentLoading(false);
    }
  }, [selectedYear, rentCampuses, setBanner]);

  useEffect(() => {
    loadRentRates();
  }, [loadRentRates]);

  const setRentRate = (campus, type, value) => {
    setRentRates((prev) => ({
      ...prev,
      [campus]: { ...(prev[campus] || {}), [type]: value },
    }));
  };

  const handleSaveRentRates = async () => {
    const rows = [];
    let invalid = false;
    Object.entries(rentRates).forEach(([campus, byType]) => {
      RENT_RESIDENT_TYPES.forEach((type) => {
        const raw = byType?.[type];
        if (raw === "" || raw === null || raw === undefined) return; // leave unset
        const rate = Number(raw);
        if (Number.isNaN(rate) || rate < 0) {
          invalid = true;
          return;
        }
        rows.push({ year: Number(selectedYear), campus, residentType: type, rate });
      });
    });
    if (invalid) {
      setBanner({ type: "error", text: "Rates must be numbers of 0 or more." });
      return;
    }
    if (!rows.length) {
      setBanner({ type: "info", text: "No rent rates to save." });
      return;
    }
    setRentSaving(true);
    try {
      await adminApi.saveRentCosts(rows);
      setBanner({ type: "success", text: `Rent rates saved for ${selectedYear}.` });
      loadRentRates();
    } catch (err) {
      console.error(err);
      setBanner({ type: "error", text: "Failed to save rent rates." });
    } finally {
      setRentSaving(false);
    }
  };
  const emptyForm = {
    id: null,
    name: "",
    arrivalDate: "",
    departureDate: "",
    active: true,
    notes: "",
    bookingWeekStartDay: "SUNDAY",
    bookingWeekEndDay: "SUNDAY",
    academicWeekStartDay: "MONDAY",
    academicWeekEndDay: "FRIDAY",
  };

  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [bootstrapResult, setBootstrapResult] = useState(null);
  const [bootstrapDialogOpen, setBootstrapDialogOpen] = useState(false);
  const [sortKey, setSortKey] = useState("arrivalDate");
  const [sortAsc, setSortAsc] = useState(false);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortAsc((prev) => !prev);
    } else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  const sortedCampuses = [...campuses].sort((a, b) => {
    const aVal = a[sortKey] || "";
    const bVal = b[sortKey] || "";
    const cmp = String(aVal).localeCompare(String(bVal));
    return sortAsc ? cmp : -cmp;
  });

  const updateField = (field) => (event) => {
    const value = field === "active" ? event.target.value === "true" : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const startEdit = (campus) => {
    setForm({
      id: campus.id,
      name: campus.name || "",
      arrivalDate: campus.arrivalDate || "",
      departureDate: campus.departureDate || "",
      active: Boolean(campus.active),
      notes: campus.notes || "",
      bookingWeekStartDay: campus.bookingWeekStartDay || "SUNDAY",
      bookingWeekEndDay: campus.bookingWeekEndDay || "SUNDAY",
      academicWeekStartDay: campus.academicWeekStartDay || "MONDAY",
      academicWeekEndDay: campus.academicWeekEndDay || "FRIDAY",
    });
  };

  const resetForm = () => setForm(emptyForm);

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      const payload = {
        name: form.name,
        arrivalDate: form.arrivalDate,
        departureDate: form.departureDate,
        active: form.active,
        notes: form.notes,
        bookingWeekStartDay: form.bookingWeekStartDay,
        bookingWeekEndDay: form.bookingWeekEndDay,
        academicWeekStartDay: form.academicWeekStartDay,
        academicWeekEndDay: form.academicWeekEndDay,
      };

      if (form.id) {
        await adminApi.updateCampus(form.id, payload);
        setBanner({ type: "success", text: `Updated campus period for ${form.name}` });
      } else {
        await adminApi.createCampus(payload);
        setBanner({ type: "success", text: `Created campus period for ${form.name}` });
      }

      resetForm();
      onRefresh?.();
    } catch (error) {
      setBanner({ type: "error", text: error.message || "Failed to save campus" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (campus) => {
    const confirmed = window.confirm(
      `Delete ${campus.name} (${campus.arrivalDate} to ${campus.departureDate})?`
    );
    if (!confirmed) return;

    try {
      await adminApi.deleteCampus(campus.id);
      setBanner({ type: "success", text: `Deleted ${campus.name}` });

      if (String(form.id) === String(campus.id)) {
        resetForm();
      }

      onRefresh?.();
    } catch (error) {
      setBanner({ type: "error", text: error.message || "Failed to delete campus" });
    }
  };

  const handleBootstrap = async () => {
    setBootstrapping(true);
    setBootstrapResult(null);
    try {
      const result = await adminApi.bootstrapCalendarWeeks();
      setBootstrapResult(result);
      setBootstrapDialogOpen(true);
      setBanner({ type: "success", text: "Calendar weeks regenerated." });
    } catch (error) {
      setBanner({ type: "error", text: error.message || "Failed to bootstrap calendar weeks" });
    } finally {
      setBootstrapping(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Box component="form" onSubmit={handleSave}>
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} useFlexGap flexWrap="wrap">
            <TextField
              label="Campus name"
              value={form.name}
              onChange={updateField("name")}
              size="small"
              required
              sx={{ ...FIELD_SX, minWidth: 220 }}
            />
            <TextField
              label="Arrival date"
              type="date"
              value={form.arrivalDate}
              onChange={updateField("arrivalDate")}
              size="small"
              required
              InputLabelProps={{ shrink: true }}
              sx={{ ...FIELD_SX, minWidth: 170 }}
            />
            <TextField
              label="Departure date"
              type="date"
              value={form.departureDate}
              onChange={updateField("departureDate")}
              size="small"
              required
              InputLabelProps={{ shrink: true }}
              sx={{ ...FIELD_SX, minWidth: 170 }}
            />
            <TextField
              select
              label="Active"
              value={String(form.active)}
              onChange={updateField("active")}
              size="small"
              sx={{ ...FIELD_SX, minWidth: 120 }}
            >
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </TextField>
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} useFlexGap flexWrap="wrap">
            <TextField
              select
              label="Booking week starts"
              value={form.bookingWeekStartDay}
              onChange={updateField("bookingWeekStartDay")}
              size="small"
              sx={{ ...FIELD_SX, minWidth: 180 }}
            >
              {DAYS.map((d) => (
                <MenuItem key={d} value={d}>{formatDay(d)}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Booking week ends"
              value={form.bookingWeekEndDay}
              onChange={updateField("bookingWeekEndDay")}
              size="small"
              sx={{ ...FIELD_SX, minWidth: 180 }}
            >
              {DAYS.map((d) => (
                <MenuItem key={d} value={d}>{formatDay(d)}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Academic week starts"
              value={form.academicWeekStartDay}
              onChange={updateField("academicWeekStartDay")}
              size="small"
              sx={{ ...FIELD_SX, minWidth: 180 }}
            >
              {DAYS.map((d) => (
                <MenuItem key={d} value={d}>{formatDay(d)}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Academic week ends"
              value={form.academicWeekEndDay}
              onChange={updateField("academicWeekEndDay")}
              size="small"
              sx={{ ...FIELD_SX, minWidth: 180 }}
            >
              {DAYS.map((d) => (
                <MenuItem key={d} value={d}>{formatDay(d)}</MenuItem>
              ))}
            </TextField>
          </Stack>

          <TextField
            label="Notes"
            value={form.notes}
            onChange={updateField("notes")}
            size="small"
            multiline
            minRows={3}
            fullWidth
          />

          <Stack direction="row" spacing={1}>
            <Button type="submit" variant="contained" disabled={saving} sx={{ height: 40 }}>
              {saving ? "Saving..." : form.id ? "Update campus" : "Create campus"}
            </Button>
            <Button onClick={resetForm} disabled={saving} sx={{ height: 40 }}>
              Clear
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Divider />

      {loading ? (
        <Box py={4} display="flex" justifyContent="center">
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortKey === "name"}
                    direction={sortKey === "name" && !sortAsc ? "desc" : "asc"}
                    onClick={() => handleSort("name")}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortKey === "arrivalDate"}
                    direction={sortKey === "arrivalDate" && !sortAsc ? "desc" : "asc"}
                    onClick={() => handleSort("arrivalDate")}
                  >
                    Arrival
                  </TableSortLabel>
                </TableCell>
                <TableCell>Departure</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Booking week</TableCell>
                <TableCell>Academic week</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedCampuses.map((campus) => (
                <TableRow key={campus.id} hover>
                  <TableCell>{campus.name}</TableCell>
                  <TableCell>{campus.arrivalDate}</TableCell>
                  <TableCell>{campus.departureDate}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={campus.active ? "Active" : "Inactive"}
                      color={campus.active ? "success" : "default"}
                    />
                  </TableCell>
                  <TableCell>
                    {formatDay(campus.bookingWeekStartDay)}–{formatDay(campus.bookingWeekEndDay)}
                  </TableCell>
                  <TableCell>
                    {formatDay(campus.academicWeekStartDay)}–{formatDay(campus.academicWeekEndDay)}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>{campus.notes}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" onClick={() => startEdit(campus)} sx={{ height: 32 }}>
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleDelete(campus)}
                        sx={{ height: 32 }}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}

              {campuses.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No campuses found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Divider />

      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Rent rates — {selectedYear} (£ per resident, per night)
        </Typography>
        {rentLoading ? (
          <CircularProgress size={20} />
        ) : (
          <>
            <TableContainer component={Paper} sx={{ maxWidth: 720 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Campus</TableCell>
                    <TableCell align="right">Student</TableCell>
                    <TableCell align="right">Adult</TableCell>
                    <TableCell align="right">Staff</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rentCampuses.map((c) => (
                    <TableRow key={c.id ?? c.name}>
                      <TableCell>{c.name}</TableCell>
                      {RENT_RESIDENT_TYPES.map((type) => (
                        <TableCell key={type} align="right">
                          <TextField
                            type="number"
                            size="small"
                            value={rentRates[c.name]?.[type] ?? ""}
                            onChange={(e) => setRentRate(c.name, type, e.target.value)}
                            inputProps={{
                              min: 0,
                              step: "0.01",
                              style: { textAlign: "right", width: 70 },
                            }}
                            sx={FIELD_SX}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                  {rentCampuses.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No campuses running in {selectedYear}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "text.secondary" }}>
              Blank = leave unchanged. Enter 0 to set a rate of zero explicitly.
            </Typography>
            <Button
              variant="contained"
              size="small"
              sx={{ mt: 1 }}
              onClick={handleSaveRentRates}
              disabled={rentSaving || rentLoading}
            >
              {rentSaving ? "Saving…" : "Save rent rates"}
            </Button>
          </>
        )}
      </Box>

      <Divider />

      <Box sx={{ p: 2, border: "1px solid #f44336", borderRadius: 1, bgcolor: "#ffebee" }}>
        <Typography variant="subtitle2" sx={{ color: "#b71c1c", mb: 1 }}>
          Calendar weeks bootstrap
        </Typography>
        <Typography variant="caption" sx={{ display: "block", mb: 1 }}>
          Regenerates calendar weeks for all campuses based on their arrival/departure
          dates and configured week start/end days. Run after editing campus dates or
          week day settings.
        </Typography>
        <Button
          color="error"
          variant="outlined"
          size="small"
          onClick={handleBootstrap}
          disabled={bootstrapping}
        >
          {bootstrapping ? "Running..." : "Bootstrap calendar weeks"}
        </Button>
      </Box>

      <Dialog
        open={bootstrapDialogOpen}
        onClose={() => setBootstrapDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Calendar weeks bootstrap result</DialogTitle>
        <DialogContent>
          {bootstrapResult ? (
            <Stack spacing={2}>
              <DialogContentText>
                Created: {bootstrapResult.summary?.createdCampuses ?? 0} ·
                Schedule updated: {bootstrapResult.summary?.scheduleUpdatedCampuses ?? 0} ·
                Regenerated: {bootstrapResult.summary?.regeneratedCampuses ?? 0}
              </DialogContentText>

              {bootstrapResult.createdCampuses?.length > 0 && (
                <Box>
                  <Typography variant="subtitle2">Created</Typography>
                  <Box component="ul" sx={{ pl: 3, m: 0 }}>
                    {bootstrapResult.createdCampuses.map((c) => (
                      <li key={`c-${c}`}><Typography variant="body2">{c}</Typography></li>
                    ))}
                  </Box>
                </Box>
              )}

              {bootstrapResult.scheduleUpdatedCampuses?.length > 0 && (
                <Box>
                  <Typography variant="subtitle2">Schedule updated</Typography>
                  <Box component="ul" sx={{ pl: 3, m: 0 }}>
                    {bootstrapResult.scheduleUpdatedCampuses.map((c) => (
                      <li key={`s-${c}`}><Typography variant="body2">{c}</Typography></li>
                    ))}
                  </Box>
                </Box>
              )}

              {bootstrapResult.regeneratedCampuses?.length > 0 && (
                <Box>
                  <Typography variant="subtitle2">Regenerated</Typography>
                  <Box component="ul" sx={{ pl: 3, m: 0 }}>
                    {bootstrapResult.regeneratedCampuses.map((c) => (
                      <li key={`r-${c}`}><Typography variant="body2">{c}</Typography></li>
                    ))}
                  </Box>
                </Box>
              )}
            </Stack>
          ) : (
            <DialogContentText>No result to display.</DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBootstrapDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
